import React, { useState } from 'react';
import { X, DollarSign, Video } from 'lucide-react';

const VideoDepositModal = ({ isOpen, onClose }) => {
  const [selectedPackage, setSelectedPackage] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    instructions: ''
  });

  const packages = [
    { id: 0, name: 'Reels', price: 100, deposit: 50, displayPrice: '$100' },
    { id: 1, name: 'Mic Videos', price: 150, deposit: 75, displayPrice: '$150' },
    { id: 2, name: 'Music Video (Standard)', price: 250, deposit: 125, displayPrice: '$250' },
    { id: 3, name: 'Music Video (Heavy FX)', price: 350, deposit: 175, displayPrice: '$350+', isVariable: true }
  ];

  if (!isOpen) return null;

  const handleSubmit = (e) => {
    e.preventDefault();
    const pkg = packages.find(p => p.id === selectedPackage);
    alert(`Processing $${pkg.deposit} deposit for ${pkg.name}. Remaining balance ($${pkg.price - pkg.deposit}) due on-site.`);
  };

  const selectedPkg = packages.find(p => p.id === selectedPackage);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm">
      <div className="relative w-full max-w-2xl max-h-[90vh] overflow-y-auto bg-gradient-to-br from-purple-900/40 to-blue-900/40 backdrop-blur-xl border border-purple-500/30 rounded-2xl">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-purple-500/30 sticky top-0 bg-gradient-to-br from-purple-900/60 to-blue-900/60 backdrop-blur-xl z-10">
          <div>
            <h2 className="text-2xl font-bold text-white mb-1">Video Production</h2>
            <p className="text-gray-400 text-sm">50% deposit required - Remaining payment due at the video shoot</p>
          </div>
          <button
            onClick={onClose}
            className="w-10 h-10 rounded-full bg-purple-900/30 hover:bg-purple-900/50 flex items-center justify-center transition-colors"
          >
            <X className="text-white" size={20} />
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          {/* Package Selection */}
          <div className="mb-6">
            <h3 className="text-white font-bold text-lg mb-4">Select Video Package</h3>
            <div className="space-y-3">
              {packages.map((pkg) => (
                <button
                  key={pkg.id}
                  onClick={() => setSelectedPackage(pkg.id)}
                  className={`w-full p-4 rounded-lg border-2 transition-all text-left ${
                    selectedPackage === pkg.id
                      ? 'border-purple-500 bg-purple-600/20'
                      : 'border-purple-500/20 hover:border-purple-500/50 bg-black/20'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="text-white font-semibold mb-1">{pkg.name}</div>
                      <div className="text-sm text-gray-400">
                        Total: {pkg.displayPrice || `$${pkg.price}`} • Deposit: ${pkg.deposit}{pkg.isVariable && '+'}
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="text-xs text-gray-400">Deposit</div>
                      <div className="text-2xl font-bold text-green-400">${pkg.deposit}{pkg.isVariable && '+'}</div>
                    </div>
                  </div>
                </button>
              ))}
            </div>
          </div>

          {/* Deposit Breakdown */}
          {selectedPkg && (
            <div className="bg-gradient-to-br from-green-900/20 to-blue-900/20 border border-green-500/30 rounded-xl p-6 mb-6">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center space-x-2">
                  <Video className="text-green-400" size={20} />
                  <h3 className="text-white font-bold">{selectedPkg.name}</h3>
                </div>
              </div>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between text-gray-300">
                  <span>Total Package Price</span>
                  <span>{selectedPkg.displayPrice || `$${selectedPkg.price}`}</span>
                </div>
                <div className="flex justify-between text-green-400 font-semibold">
                  <span>50% Deposit Required</span>
                  <span>${selectedPkg.deposit}{selectedPkg.isVariable && '+'}</span>
                </div>
                {selectedPkg.isVariable && (
                  <div className="bg-blue-900/20 border border-blue-500/30 rounded-lg p-3 my-2">
                    <p className="text-xs text-blue-300">
                      <strong>Note:</strong> Final price determined by project scope. Deposit starts at $175 (50% of minimum $350).
                    </p>
                  </div>
                )}
                <div className="border-t border-purple-500/20 pt-2 mt-2">
                  <div className="flex justify-between text-yellow-400 font-semibold">
                    <span>Remaining Due at the Video Shoot</span>
                    <span>${selectedPkg.price - selectedPkg.deposit}{selectedPkg.isVariable && '+'}</span>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-4">
            <input
              type="text"
              placeholder="Your Name"
              required
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              className="w-full px-4 py-3 bg-black/40 border border-purple-500/20 rounded-lg text-white placeholder-gray-500 focus:border-purple-500 focus:outline-none focus:ring-2 focus:ring-purple-500/20"
            />
            <input
              type="email"
              placeholder="Email Address"
              required
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              className="w-full px-4 py-3 bg-black/40 border border-purple-500/20 rounded-lg text-white placeholder-gray-500 focus:border-purple-500 focus:outline-none focus:ring-2 focus:ring-purple-500/20"
            />
            <input
              type="tel"
              placeholder="Phone Number"
              required
              value={formData.phone}
              onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
              className="w-full px-4 py-3 bg-black/40 border border-purple-500/20 rounded-lg text-white placeholder-gray-500 focus:border-purple-500 focus:outline-none focus:ring-2 focus:ring-purple-500/20"
            />
            <textarea
              placeholder="Video concept, instructions, or special requests..."
              rows={4}
              value={formData.instructions}
              onChange={(e) => setFormData({ ...formData, instructions: e.target.value })}
              className="w-full px-4 py-3 bg-black/40 border border-purple-500/20 rounded-lg text-white placeholder-gray-500 focus:border-purple-500 focus:outline-none focus:ring-2 focus:ring-purple-500/20 resize-none"
            ></textarea>

            <button
              type="submit"
              disabled={!selectedPackage}
              className="w-full px-6 py-4 bg-gradient-to-r from-purple-600 to-blue-600 text-white rounded-lg font-semibold hover:from-purple-700 hover:to-blue-700 transition-all flex items-center justify-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <DollarSign size={20} />
              <span>Pay ${selectedPkg ? selectedPkg.deposit : '---'} Deposit</span>
            </button>
          </form>

          <div className="mt-6 bg-yellow-900/20 border border-yellow-500/30 rounded-lg p-4">
            <p className="text-sm text-gray-200 mb-2">
              <span className="font-semibold text-yellow-400">💳 Payment Terms:</span>
            </p>
            <ul className="text-xs text-gray-300 space-y-1 ml-4">
              <li>• 50% deposit secures your video booking</li>
              <li>• <span className="font-semibold text-yellow-400">Remaining payment due at the video shoot</span></li>
              <li>• Cash, card, or digital payment accepted for balance</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VideoDepositModal;
